import { useState } from 'react';
import type { EmployeeScope, Team, User } from '../api.js';
import { partition, shouldRevealInactive } from '../filtering.js';
import { formatHoursTotal, lastDay, nextDay } from '../format.js';
import { PERIOD_PRESETS, matchPreset, type Period } from '../periods.js';
import { partitionUsers } from '../users.js';

/**
 * Списки «Команды» и «Сотрудники» убраны с экрана (замечание заказчика
 * 2026-10-21): на вкладке их называли сбивающими с толку — названия команд
 * в них те же, что в таблице, и выбирать разрез по ним никто не просил.
 *
 * Мягкое удаление: код и состояние фильтров живут, URL с `?teams=` и
 * `?employees=` по-прежнему работает и сужает таблицу. Вернуть экран — это
 * `true` здесь, а не восстановление по истории git.
 */
const SELECTORS_VISIBLE = false;

interface Props {
  period: Period;
  teams: Team[];
  users: User[];
  selectedTeams: string[];
  selectedAuthors: string[];
  onPeriodChange: (period: Period) => void;
  onTeamsChange: (teams: string[]) => void;
  onAuthorsChange: (keys: string[]) => void;
  employeeScope: EmployeeScope;
  onEmployeeScopeChange: (scope: EmployeeScope) => void;
}

/**
 * Кого показывать строками матрицы. Нулевая строка — это ответ, а не пустое
 * место: «человек обычно списывает, а за этот месяц ничего». Всех работающих
 * подряд по умолчанию не показываем — 92 учётки не списывали никогда, и матрица
 * превратилась бы в поле прочерков.
 */
const SCOPES: { value: EmployeeScope; label: string; hint: string }[] = [
  { value: 'none', label: 'только с часами', hint: 'кто списал хоть что-то за период' },
  {
    value: 'logging',
    label: '+ кто списывает обычно',
    hint: 'работающие учётки, у которых есть списания в истории — их ноль за период виден строкой',
  },
  {
    value: 'active',
    label: 'все работающие',
    hint: 'все активные учётки Jira, включая тех, кто не списывал ни часа никогда',
  },
];

/** Колонок в сетке фильтра; при узком экране CSS возвращает обычный поток. */
const FILTER_COLUMNS = 4;

/**
 * Высота колонки для раскладки сверху вниз. `grid-auto-flow: column` требует
 * знать число строк: без него браузер разложит элементы по строкам, и алфавит
 * пойдёт слева направо, а не по колонке.
 */
function columnRows(count: number): { gridTemplateRows: string } {
  return { gridTemplateRows: `repeat(${Math.max(1, Math.ceil(count / FILTER_COLUMNS))}, auto)` };
}

function toggle(list: string[], value: string): string[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

export function Filters({
  period,
  teams,
  users,
  selectedTeams,
  selectedAuthors,
  onPeriodChange,
  onTeamsChange,
  onAuthorsChange,
  employeeScope,
  onEmployeeScopeChange,
}: Props) {
  const [teamSearch, setTeamSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [teamsOpen, setTeamsOpen] = useState(false);
  const [inactiveOpen, setInactiveOpen] = useState(false);
  const activePreset = matchPreset(period);

  // Список приходит за период, но выбранная команда может из него выпасть —
  // тогда снять галочку было бы нечем. Дописываем её с нулём часов.
  const knownTeams = [
    ...teams,
    ...selectedTeams
      .filter((team) => !teams.some((known) => known.team === team))
      .map((team) => ({
        team,
        label: team === '' ? 'без команды' : team,
        seconds: 0,
        totalSeconds: 0,
      })),
  ];

  // По алфавиту, «без команды» в конце: это не команда, а незаполненное поле,
  // и место ему после списка, а не между `Academic Division` и `Data Team`.
  const sortedTeams = [...knownTeams].sort((a, b) => {
    if (a.team === '') return 1;
    if (b.team === '') return -1;
    return a.label.localeCompare(b.label, 'ru');
  });

  // Деление то же, что у сотрудников: работавшие в периоде сразу, спавшие —
  // под раскрывашкой. «Активность» здесь про период, а не про саму команду:
  // в июле пусто, в августе 227 часов — это одна и та же живая команда.
  const byTeam = partition(sortedTeams, {
    search: teamSearch,
    selected: selectedTeams,
    isActive: (team) => team.seconds > 0,
    id: (team) => team.team,
    text: (team) => [team.label],
  });
  const teamsForcedOpen = shouldRevealInactive(byTeam, teamSearch);
  const showSleepingTeams = teamsOpen || teamsForcedOpen;

  const byUser = partitionUsers(users, userSearch, selectedAuthors);
  const usersForcedOpen = shouldRevealInactive(byUser, userSearch);
  const showInactiveUsers = inactiveOpen || usersForcedOpen;

  return (
    <section className="filters">
      <div className="filter-block">
        <div className="filter-label">Период</div>
        <div className="filter-line">
          {PERIOD_PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              className={activePreset === preset.id ? 'chip chip-on' : 'chip'}
              onClick={() => onPeriodChange(preset.build(new Date()))}
            >
              {preset.label}
            </button>
          ))}
          <span className="filter-sep" />
          <label>
            с
            <input
              type="date"
              value={period.from}
              max={period.to}
              onChange={(e) => onPeriodChange({ ...period, from: e.target.value })}
            />
          </label>
          <label>
            по
            {/* Показываем последний день периода, а не границу `[from, to)`:
                «по 01.10» читается как лишний день и вводит в заблуждение
                (замечание заказчика 2026-10-21). В состояние уходит прежняя
                граница — меняется подпись, а не арифметика. */}
            <input
              type="date"
              value={lastDay(period.to)}
              min={period.from}
              onChange={(e) => onPeriodChange({ ...period, to: nextDay(e.target.value) })}
            />
          </label>
        </div>
      </div>

      {SELECTORS_VISIBLE && (
        <>
          <div className="filter-block">
            <div className="filter-label">
              Команды
              <span className="count">
                {byTeam.active.length} с часами за период из {knownTeams.length}
              </span>
              {selectedTeams.length > 0 && (
                <button type="button" className="link" onClick={() => onTeamsChange([])}>
                  сбросить ({selectedTeams.length})
                </button>
              )}
            </div>
            <input
              type="search"
              className="search"
              placeholder="Поиск по названию команды"
              value={teamSearch}
              onChange={(e) => setTeamSearch(e.target.value)}
            />
            {/* Колонками, а не строками: список читают сверху вниз, и алфавит
              должен идти по колонке. Число строк считается здесь — CSS сам
              распределить не может, не зная длины списка. */}
            <div
              className="option-grid by-column scroll-box"
              style={columnRows(byTeam.active.length)}
            >
              {byTeam.active.map((team) => (
                <TeamOption
                  key={team.team}
                  team={team}
                  checked={selectedTeams.includes(team.team)}
                  onToggle={() => onTeamsChange(toggle(selectedTeams, team.team))}
                />
              ))}
              {byTeam.active.length === 0 && (
                <span className="muted small">за период ни одна команда не подошла</span>
              )}
            </div>

            {byTeam.inactive.length > 0 && (
              <div className="inactive-block">
                {teamsForcedOpen ? (
                  // Не кнопка: закрыть сейчас значит спрятать совпадение или выбранное.
                  <div className="disclosure as-label">
                    <span className="disclosure-arrow">▾</span>
                    Без часов за период ({byTeam.inactive.length})
                    {byTeam.selectedInactive > 0 && (
                      <span className="badge">выбрано {byTeam.selectedInactive}</span>
                    )}
                  </div>
                ) : (
                  <button
                    type="button"
                    className="disclosure"
                    aria-expanded={teamsOpen}
                    onClick={() => setTeamsOpen(!teamsOpen)}
                  >
                    <span className="disclosure-arrow">{teamsOpen ? '▾' : '▸'}</span>
                    Без часов за период ({byTeam.inactive.length})
                  </button>
                )}

                {showSleepingTeams && (
                  <>
                    {/* Команда без часов в периоде — не мёртвая: в соседнем месяце
                      у неё может быть сотня часов, и выбрать её нужно уметь. */}
                    <div className="muted small">
                      В этом периоде списаний нет. В подсказке — часы за всю историю
                    </div>
                    <div
                      className="option-grid by-column scroll-box tall"
                      style={columnRows(byTeam.inactive.length)}
                    >
                      {byTeam.inactive.map((team) => (
                        <TeamOption
                          key={team.team}
                          team={team}
                          flagged
                          checked={selectedTeams.includes(team.team)}
                          onToggle={() => onTeamsChange(toggle(selectedTeams, team.team))}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="filter-block">
            <div className="filter-label">
              Сотрудники
              <span className="count">{byUser.active.length} работающих с часами</span>
              {selectedAuthors.length > 0 && (
                <button type="button" className="link" onClick={() => onAuthorsChange([])}>
                  сбросить ({selectedAuthors.length})
                </button>
              )}
            </div>

            <div className="chip-group">
              <div className="chip-group-label">Показывать в таблице</div>
              <div className="chips">
                {SCOPES.map((scope) => (
                  <button
                    key={scope.value}
                    type="button"
                    className={`chip${employeeScope === scope.value ? ' chip-on' : ''}`}
                    title={scope.hint}
                    onClick={() => onEmployeeScopeChange(scope.value)}
                  >
                    {scope.label}
                  </button>
                ))}
              </div>
            </div>
            <input
              type="search"
              className="search"
              placeholder="Поиск по имени или логину"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
            />
            <div className="option-grid scroll-box">
              {byUser.active.map((user) => (
                <UserOption
                  key={user.userKey}
                  user={user}
                  checked={selectedAuthors.includes(user.userKey)}
                  onToggle={() => onAuthorsChange(toggle(selectedAuthors, user.userKey))}
                />
              ))}
              {byUser.active.length === 0 && (
                <span className="muted small">среди работающих никого не найдено</span>
              )}
            </div>

            {byUser.inactive.length > 0 && (
              <div className="inactive-block">
                {usersForcedOpen ? (
                  // Не кнопка: закрыть сейчас значит спрятать совпадение или выбранного человека.
                  <div className="disclosure as-label">
                    <span className="disclosure-arrow">▾</span>
                    Неактивные ({byUser.inactive.length})
                    {byUser.selectedInactive > 0 && (
                      <span className="badge">выбрано {byUser.selectedInactive}</span>
                    )}
                  </div>
                ) : (
                  <button
                    type="button"
                    className="disclosure"
                    aria-expanded={inactiveOpen}
                    onClick={() => setInactiveOpen(!inactiveOpen)}
                  >
                    <span className="disclosure-arrow">{inactiveOpen ? '▾' : '▸'}</span>
                    Неактивные ({byUser.inactive.length})
                  </button>
                )}

                {showInactiveUsers && (
                  <>
                    {/* Уволенные тоже держат часы: человек мог уйти посреди периода. */}
                    <div className="muted small">Их часы учитываются в таблице</div>
                    <div className="option-grid scroll-box tall">
                      {byUser.inactive.map((user) => (
                        <UserOption
                          key={user.userKey}
                          user={user}
                          flagged
                          checked={selectedAuthors.includes(user.userKey)}
                          onToggle={() => onAuthorsChange(toggle(selectedAuthors, user.userKey))}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}

interface OptionProps {
  checked: boolean;
  onToggle: () => void;
  flagged?: boolean;
}

function TeamOption({ team, checked, onToggle, flagged }: OptionProps & { team: Team }) {
  return (
    <label className={flagged ? 'option option-flagged' : 'option'}>
      <input type="checkbox" checked={checked} onChange={onToggle} />
      {/* В списке главная команда: подразделения свёрнуты в неё, и
          `Mental Division > Development` считается как `Mental Division`. */}
      <span className={team.team === '' ? 'option-name muted' : 'option-name'}>{team.label}</span>
      <span
        className={team.seconds > 0 ? 'option-key' : 'option-key option-key-zero'}
        title={
          team.seconds > 0
            ? 'Часы за выбранный период'
            : `За период часов нет; за всё время ${formatHoursTotal(team.totalSeconds)} ч`
        }
      >
        {team.seconds > 0 ? formatHoursTotal(team.seconds) : '—'}
      </span>
    </label>
  );
}

function UserOption({ user, checked, onToggle, flagged }: OptionProps & { user: User }) {
  return (
    <label className={flagged ? 'option option-flagged' : 'option'}>
      <input type="checkbox" checked={checked} onChange={onToggle} />
      <span className="option-name">{user.displayName}</span>
      <span className="option-note">{user.userKey}</span>
    </label>
  );
}
